<template lang="pug">
    //- The WhatsApp message card exactly as the customer receives it: optional image/text
    //- header, body, footer, and the action buttons. One shared renderer so the template
    //- authoring preview, the send composer preview, and the quoted-reply snippet never drift.
    //- `framed` adds the shared green preview panel + "Vorschau" label so every authoring /
    //- composer preview wears one chrome; bubble + compact render bare (no frame).
    .wa-card-preview(:class="{ 'wa-card-preview--framed': framed }")
        span.wa-card-preview__label(v-if="framed") {{ $t('Preview') }}
        .wa-card(:class="{ 'wa-card--compact': compact, 'wa-card--bubble': bubble }")
            img.wa-card__image(v-if="headerImage" :src="headerImage")
            .wa-card__header(v-else-if="header") {{ header }}
            .wa-card__body(v-if="body" v-html="body")
            .wa-card__footer(v-if="footer") {{ footer }}
            .wa-card__buttons(v-if="showButtons")
                //- URL buttons become real links when linkButtons is set (the thread lets you open
                //- them); otherwise every button is a display-only span, as in the authoring previews.
                component.wa-card__button(
                    v-for="(button, index) in buttons"
                    :key="index"
                    :is="linkButtons && button.url ? 'a' : 'span'"
                    :href="linkButtons && button.url ? button.url : null"
                    :target="linkButtons && button.url ? '_blank' : null"
                    rel="noopener"
                )
                    i.el-icon-fa-arrow-up-right-from-square.fas(v-if="button.type === 'URL'")
                    i.el-icon-fa-phone.fas(v-else-if="button.type === 'PHONE_NUMBER'")
                    i.el-icon-fa-reply.fas(v-else)
                    | {{ button.text }}
</template>

<i18n>
{
    "de": {
        "Preview": "Vorschau"
    }
}
</i18n>

<script>
export default {
    name: 'WhatsAppCardPreview',
    props: {
        // A media header (image url); wins over a text header when set.
        headerImage: { type: String, default: '' },
        header: { type: String, default: '' },
        // The message body — HTML (the authoring preview highlights filled pills; the
        // composer/quote pass plain text, which renders verbatim).
        body: { type: String, default: '' },
        footer: { type: String, default: '' },
        buttons: { type: Array, default: () => [] },
        // Compact = the quoted-reply snippet: muted, left-bordered, no buttons.
        compact: { type: Boolean, default: false },
        // Bubble = rendered inside a chat bubble (the thread): no card chrome, inherits the
        // bubble's colour + size, buttons bleed to the bubble edges.
        bubble: { type: Boolean, default: false },
        // Render URL buttons as real <a> links (the thread lets you open them); default is
        // display-only spans, as the authoring previews want.
        linkButtons: { type: Boolean, default: false },
        // Wrap the card in the shared green preview panel + "Vorschau" label (authoring/composer
        // previews). Off for the thread bubble + quoted-reply, which live inside a chat bubble.
        framed: { type: Boolean, default: false }
    },
    computed: {
        // Compact (quoted reply) hides buttons; the card + bubble variants show them.
        showButtons() {
            return !this.compact && this.buttons.length > 0
        }
    }
}
</script>

<style lang="sass" scoped>
// Real WhatsApp Web light-theme values so the previews AND the thread bubbles reproduce the
// card the customer actually sees: incoming/card #fff, text #111b21, meta #667781, action
// blue #027eb5, radius 7.5px, WhatsApp shadow + font stack.
$wa-font: -apple-system, "Segoe UI", "Helvetica Neue", Helvetica, Arial, sans-serif
$wa-text: #111b21
$wa-meta: #667781
$wa-action: #027eb5
$wa-shadow: 0 1px .5px rgba(11, 20, 26, .13)
$wa-line: rgba(0, 0, 0, .09)

// The framed preview: the card sits on WhatsApp's chat wallpaper (beige) so it reads exactly
// as the customer sees it in WhatsApp, with a small "Vorschau" label above.
.wa-card-preview--framed
    // Own line-height so an Element form-item ancestor (line-height: 36px on .el-form-item__content)
    // can't inherit down and inflate the label + footer.
    line-height: 1.4
    // Match Element's form-item rhythm so the panel doesn't sit flush against the next field.
    margin-bottom: 22px
    padding: 14px
    border-radius: 8px
    background: #efeae2
    border: 1px solid rgba(0, 0, 0, .06)
    .wa-card-preview__label
        display: block
        margin-bottom: 10px
        font-size: .7rem
        font-weight: 600
        letter-spacing: .04em
        text-transform: uppercase
        color: #667781

// Standalone card = the WhatsApp INCOMING message (white bubble) as the customer receives it.
.wa-card
    max-width: 320px
    overflow: hidden               // clip bled media to the rounded corners
    font-family: $wa-font
    font-size: 14.2px
    line-height: 19px
    color: $wa-text
    padding: 6px 9px 8px
    border-radius: 7.5px
    background: #fff
    box-shadow: $wa-shadow
    .wa-card__image
        display: block
        // Bleed to the card edges (WhatsApp media header sits flush at the top).
        width: calc(100% + 18px)
        margin: -6px -9px 6px
        aspect-ratio: 4 / 1
        object-fit: cover
    .wa-card__header
        font-weight: 600
        margin-bottom: 4px
    .wa-card__body
        white-space: pre-wrap
        ::v-deep .wa-fill
            font-weight: 600
            color: #059669
    .wa-card__footer
        margin-top: 4px
        font-size: 13px
        color: $wa-meta
    .wa-card__buttons
        display: flex
        flex-direction: column
        margin: 6px -9px -8px
        border-top: 1px solid $wa-line
        .wa-card__button
            display: flex
            align-items: center
            justify-content: center
            gap: 7px
            padding: 9px
            line-height: 1.2
            font-size: 14px
            color: $wa-action
            border-top: 1px solid $wa-line
            &:first-child
                border-top: none
            i
                font-size: 13px

// Bubble: inside a chat bubble — bare, inherits the bubble's colour/size; media + buttons bleed
// to the bubble's padding edges (the bubble owns overflow: hidden + the corner radii).
.wa-card--bubble
    max-width: none
    overflow: visible
    padding: 0
    border: none
    border-radius: 0
    background: transparent
    box-shadow: none
    font-family: inherit
    font-size: inherit
    line-height: inherit
    color: inherit
    .wa-card__image
        width: calc(100% + 18px)
        margin: -6px -9px 5px
    .wa-card__footer
        margin-top: 3px
        font-size: 13px
        color: $wa-meta
    .wa-card__buttons
        gap: 0
        margin: 5px -9px -8px
        border-top: 1px solid rgba(0, 0, 0, .13)
        .wa-card__button
            padding: 9px
            line-height: 1.2
            font-size: 14px
            color: $wa-action
            text-decoration: none
            border-top: 1px solid $wa-line
            &:first-child
                border-top: none

// Quoted-reply snippet: WhatsApp's inset reply preview.
.wa-card--compact
    max-width: none
    overflow: hidden
    padding: 4px 8px
    border: none
    border-left: 4px solid rgba(0, 0, 0, .2)
    border-radius: 4px
    background: rgba(0, 0, 0, .05)
    box-shadow: none
    .wa-card__image
        width: 100%
        margin: 0 0 4px
        aspect-ratio: 4 / 1
        border-radius: 4px
    .wa-card__header
        font-size: 13px
        margin-bottom: 2px
    .wa-card__body
        font-size: 13px
        line-height: 1.35
        color: $wa-meta
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis
    .wa-card__footer
        font-size: 12px
        margin-top: 2px
</style>
